import axios from "./axiosInstance";
import config from "./config";
import { capturePaymentError } from "../utils/sentry";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

/**
 * The ONE failure envelope for this module.
 *
 * A caller can only read one shape, so a function whose failure shape does not
 * MIRROR its own success shape silently swallows the reason. That is not
 * hypothetical: acceptPayment resolved the axios response on success (flag at
 * res.data.success) but a FLAT { success, error } from its catch, and its one
 * caller — correctly written for the success shape — read res.data.error,
 * found nothing, and showed "Failed to accept payment" instead of the
 * backend's "That receipt number is already used." Staff saw that generic
 * string for six months while the real reason was sitting on res.error.
 *
 * So: resolve the axios RESPONSE on success -> use failureResponse.
 *     resolve res.data (the BODY) on success -> use failureBody.
 * Either way `success` and `error` land where that function's callers
 * already look. Do not reintroduce a second shape (see createPayment above).
 */
const failureMessage = (err) =>
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  err?.message ||
  "Request failed";

/**
 * For functions whose success value is res.data — the body itself.
 *
 * The backend's OWN body is returned untouched when there is one. It already
 * reads as a failure (no `success: true`) and it carries fields callers use:
 * Stripe's `code` and `decline_code` reach the decline messaging through here,
 * and rewriting them into a bare { success, error } silently dropped them.
 *
 * The synthesized object is for the case that had no answer at all — a network
 * error, a timeout, a CORS rejection — where err.response is undefined and
 * this used to hand the caller `undefined` to read `.error` off.
 */
const failureBody = (err, extra = null) => {
  const body = err?.response?.data;
  if (body && typeof body === "object") return extra ? { ...body, ...extra } : body;
  return { success: false, error: failureMessage(err), ...(extra || {}) };
};

/**
 * For functions whose success value is the whole axios response, so callers
 * read res.data.success / res.data.error.
 */
const failureResponse = (err) => ({ data: failureBody(err) });


// POST Create a new payment
/**
 * Record a payment. `membershipId` AND `invoiceId` are both REQUIRED — the
 * controller 400s without them (paymentController.js:439).
 *
 * FAILURE SHAPE IS NORMALISED. This used to resolve `{ success: false, error }`
 * at the TOP level, while success resolved the axios response with the flag at
 * `res.data.success`. Two different shapes for one call is what made the wrong
 * check easy to write: five call sites checked nothing at all and marked the
 * invoice paid regardless, and two card surfaces never recorded a payment.
 *
 * Now both outcomes carry the flag in the SAME place, so a single check —
 *
 *     const res = await createPayment(obj);
 *     if (!res?.data?.success) { ...do not mark anything paid... }
 *
 * — is correct whether the request failed, was rejected by the controller, or
 * succeeded. Do not reintroduce a second shape.
 */
export const createPayment = (newForm) => {
  return axios
    .post(`${BASE_URL}/payments`, newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res)
    .catch((err) => {
      captureException(err);
      return {
        data: {
          success: false,
          error: err.response?.data?.error || err.message,
        },
      };
    });
};
// POST Create a apply balance payment
export const applyBalancePayment = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/payments/apply-balance`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};
// POST Cash or a check taken at the desk for ONE existing invoice. The server
// writes the payment, its credit and the "paid" mark in one place. Resolves
// the axios RESPONSE for a success and a failure alike (see failureResponse),
// so the server's answer is always `res.data`.
export const recordInvoiceDeskPayment = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/invoice-desk-payment`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res)
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// POST A card payment the browser just completed for ONE invoice. The server
// confirms it with the card processor and records it. Same shape as above.
export const recordCardInvoicePayment = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/invoice-card-payment`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res)
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// GET Was this receipt number used for this family in the last few days? A
// warning for the form only. Never throws and never blocks: any failure reads
// as "not used".
export const checkReceiptNumber = (token, membershipId, receiptNumber) => {
  return axios
    .get(`${BASE_URL}/payments/receipt-number-check`, {
      params: { membershipId, receiptNumber },
      headers: { authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data || { success: false, usedRecently: false })
    .catch(() => { return { success: false, usedRecently: false }; });
};

// POST Create a new payment
export const acceptPayment = (token, paymentObj) => {
  // Translate frontend method name to backend format
  const payload = {
    ...paymentObj,
    // Backend expects 'bank_account' for ACH, frontend uses 'us_bank_account' (Stripe's format)
    method: paymentObj.method === 'us_bank_account' ? 'bank_account' : paymentObj.method,
  };
  return axios
    .post(`${BASE_URL}/payments/accept-payment`, payload, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// POST Create
export const createPaymentIntent = (newForm) => {
  return axios
    .post(`${BASE_URL}/payments/create-payment-intent`, newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// POST Create a new payment
export const applyDiscount = (newForm) => {
  return axios
    .post(`${BASE_URL}/payments/apply/discount`, newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// GET
// GET
export const getPaymentHistory = (token, id) => {
  return axios
    .get(`${BASE_URL}/payments/get-payment-history/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// GET
export const getInvoiceHistory = (token, id) => {
  return axios
    .get(`${BASE_URL}/payments/get-invoice-history/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// GET
export const getFilteredPayments = (token, filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();

  return axios
    .get(`${BASE_URL}/payments/filtered/payments?${queryParams}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// GET
export const getPaymentByMember = (token, id) => {
  return axios
    .get(`${BASE_URL}/payments/member/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// GET
export const getStripePaymentMethods = (token, id) => {
  return axios
    .get(`${BASE_URL}/payments/stripe/payment-methods/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// POST
export const createStripeCustomer = (newForm) => {
  return axios
    .post(`${BASE_URL}/payments/create-stripe-customer`, newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// POST the payment profile for an EXISTING account. For someone who is
// signed in (the account's own people, or staff who take payments). The
// server finds or makes the profile and saves it on the account itself;
// nothing is saved from the browser. `createStripeCustomer` above is only
// for a visitor signing up, who has no account yet.
export const ensureMembershipStripeCustomer = (token, membershipId) => {
  return axios
    .post(
      `${BASE_URL}/payments/membership-stripe-customer`,
      { membershipId },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => { captureException(err); return failureResponse(err); });
};

// POST charge Automatic Payments
// POST charge Automatic Payments
export const chargeAutomaticAnnualFees = (token) => {
  return axios
    .post(
      `${BASE_URL}/payments/charge-automatic-annual-fee`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// POST
export const generateSimpleInvoice = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/simple-manual-invoice`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return failureBody(err);
    });
};

// POST
export const chargeMemberManualPayments = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/charge-member-manual-payment`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// Member self-pay for an outstanding invoice. Backend resolves the
// caller's Stripe customer + default saved card and creates the PI
// off_session. Returns `{ requires_action, payment_intent_client_secret,
// paymentIntentId }` when SCA / 3DS is needed — caller runs
// stripe.confirmCardPayment and re-calls with paymentIntentId to finalize.
export const memberPayInvoice = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/member-pay-invoice`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      // 4xx is expected (declined, requires_action, no card on file).
      // Only ship 5xx / network errors to Sentry.
      const status = err?.response?.status;
      if (!status || status >= 500) captureException(err);
      return {
        success: false,
        ...(err?.response?.data || { error: err.message }),
      };
    });
};

// POST
// On failure, `answered` says whether the server replied. With no reply (a
// dropped connection) the payment may still have gone through, so the form
// keeps the same attempt key for the retry (utils/payTowardBalance).
export const payInAdvance = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/pay-in-advance`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return failureBody(err, { answered: !!err.response }); });
};

// POST
export const calculateInvoice = (data, token) => {
  return axios
    .post(`${BASE_URL}/payments/calculate-invoice`, data, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) {
        return res?.data;
      }
    })
    .catch((err) => {
      throw err;
    });
};

// POST
export const publishChargeCalculatedInvoice = (form, token) => {
  return axios
    .post(`${BASE_URL}/payments/publish-charge-calculated-invoice`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return failureBody(err);
    });
};

// POST
export const createOrUpdateInvoiceDraftStatus = (form, token) => {
  return axios
    .post(`${BASE_URL}/payments/draft-status`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// POST
export const processPayment = (form, token) => {
  return axios
    .post(`${BASE_URL}/payments/process-payment`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      capturePaymentError(err, {
        operation: "processPayment",
        amount: form?.amount,
        paymentIntentId: form?.paymentIntentId,
      });
      return failureBody(err);
    });
};

// POST
export const refundProcessPayment = (form, token) => {
  return axios
    .post(`${BASE_URL}/payments/process-refund`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      capturePaymentError(err, {
        operation: "refundProcessPayment",
        amount: form?.amount,
        paymentIntentId: form?.paymentIntentId,
      });
      return failureBody(err);
    });
};

/**
 * GET Get refund eligibility for a payment
 * @param {string} token - Auth token
 * @param {string} paymentId - Payment ID to check
 * @returns {Promise<any>}
 */
export const getRefundEligibility = (token, paymentId) => {
  return axios
    .get(`${BASE_URL}/payments/refund-eligibility/${paymentId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      capturePaymentError(err, {
        operation: "getRefundEligibility",
        paymentId,
      });
      return failureBody(err);
    });
};

// POST Create a Stripe SetupIntent
export const updateCard = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/update-card`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// POST Confirm the SetupIntent
export const updatePaymentMethod = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/update-payment-method`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => { captureException(err); return { success: false, error: err.response?.data?.error || err.message }; });
};

// POST Calculate enrollment fee for athlete enrollment
export const calculateEnrollmentFee = (token, payload) => {
  return axios
    .post(`${BASE_URL}/payments/calculate-enrollment-fee`, payload, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// POST "Create Invoice Only" when staff add an athlete. The server computes
// the amount from the same inputs as the quote (slotIds, startDate,
// overrides); `quotedTotal` is the total the screen showed and is only
// compared. A 409 with code QUOTE_CHANGED carries the new quote.
// Resolves the response BODY and throws on failure, like the quote above.
export const createEnrollmentInvoice = (token, payload) => {
  return axios
    .post(`${BASE_URL}/payments/enrollment-invoice`, payload, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      throw err;
    });
};

// GET Autopay status for a membership
export const getAutopayStatus = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/payments/autopay-status/${membershipId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// POST Process automatic payment for membership
export const processAutomaticPayment = (token, payload) => {
  return axios
    .post(`${BASE_URL}/payments/process-automatic-payment`, payload, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// POST Create a payment plan for an invoice
export const createPaymentPlan = (token, payload) => {
  return axios
    .post(`${BASE_URL}/payments/create-payment-plan`, payload, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// GET Scheduled payments for a membership
export const getScheduledPayments = (token, membershipId, status = null) => {
  const queryParams = status ? `?status=${status}` : '';
  return axios
    .get(`${BASE_URL}/payments/scheduled/${membershipId}${queryParams}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// DELETE Cancel a scheduled payment
export const cancelScheduledPayment = (token, paymentId) => {
  return axios
    .delete(`${BASE_URL}/payments/scheduled/${paymentId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// DELETE Remove a payment method from Stripe customer
export const deletePaymentMethod = (token, paymentMethodId) => {
  return axios
    .delete(`${BASE_URL}/payments/payment-method/${paymentMethodId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// ============================================
// ACH / Bank Account Payment Methods
// ============================================

// POST Create a SetupIntent for ACH bank account
export const createACHSetupIntent = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/create-ach-setup-intent`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// POST Verify bank account with micro-deposits
export const verifyBankAccountMicroDeposits = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/verify-bank-account`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// POST Set default payment method for a customer
export const setDefaultPaymentMethod = (token, form) => {
  return axios
    .post(`${BASE_URL}/payments/set-default-payment-method`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// GET Get all payment methods (cards and bank accounts) for a customer
export const getAllPaymentMethods = (token, customerId) => {
  return axios
    .get(`${BASE_URL}/payments/stripe/payment-methods/${customerId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// GET Get bank account verification status
export const getBankAccountStatus = (token, paymentMethodId) => {
  return axios
    .get(`${BASE_URL}/payments/bank-account-status/${paymentMethodId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// GET Get setup intent status (for micro-deposit verification)
export const getSetupIntentStatus = (token, setupIntentId) => {
  return axios
    .get(`${BASE_URL}/payments/setup-intent-status/${setupIntentId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

